/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { CaseExternalSystemChangeEvent } from 'app/cm/lib/events';
import { IcccManager } from 'app/ext/iccc/iccc-manager';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { StoreFocusEvent, ReturnFocusEvent } from 'app/shared/lib/events';
import { PlanOfCare } from 'app/ext/mcg-informed/plan-of-care';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

interface IExternalAssessmentAccess {
    cci: boolean;
    iccc: boolean;
}

@autoinject()
export class AssessmentAttachments extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    @bindable
    protected showIccc: boolean = false;

    protected attachments: ReadonlyArray<CM.CaseAssessmentAttachment> = [];
    protected allAttachments: ReadonlyArray<CM.CaseAssessmentAttachment> = [];
    protected icccAttachmentId: string | null = null;

    protected includeInactive: boolean = false;
    protected loadModal: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected externalAssessmentAccess: IExternalAssessmentAccess | null = null;

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM'
    };

    private caseExternalSystemChange: Subscription | null = null;

    protected mcgCopyright?: string;
    protected mcgCustomContentDisclaimer?: string;
    protected validate = validate;
    protected isValid: boolean = false;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $icccAttachmentModal!: IPopup;
    protected $mcgPlanOfCare!: PlanOfCare;
    protected $mcgCarePlanModal!: EssDialogCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        protected iccc: IcccManager,
        private context: CasePageContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.targetId) { throw new Error('Bindable property [target-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.config.generateAttachmentRoute) { throw new Error('Bindable property [config.generateAttachmentRoute] was not set'); }

        this.caseExternalSystemChange = this.events.subscribe(CaseExternalSystemChangeEvent, (e: CaseExternalSystemChangeEvent) => {
            this.reload();
        });

        this.service.configure(this.config);

        this.reload();
    }

    protected detached(): void {
        if(this.caseExternalSystemChange) {
            this.caseExternalSystemChange.dispose();
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadAttachments();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }

    ////****************** Functions ************************************************////

    protected getAttachmentRoute(attachmentId: string, task?: Core.Task, externalSystem?: Core.ExternalSystem): string | null {
        if(externalSystem?.shortName === "ICCC") {
            return null;
        }

        let externalSystemCode = undefined;
        if(externalSystem && externalSystem.code) {
            externalSystemCode = externalSystem.code;
        }
        if(!!this.targetId && !!this.config?.generateAttachmentRoute) {
            return this.config.generateAttachmentRoute(this.targetId, attachmentId, task, externalSystemCode);
        }

        throw new Error('Unable to generate attachment route');
    }

    public async reload(): Promise<void> {
        await Promise.all([this.loadAttachments(), this.loadAllAttachments()]);
    }

    protected openModal(): void {
        this.loadModal = true;
        // Set generalized copyright text
        const copyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();
        const edition = getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);

        this.$modal.open();
    }
    protected closeModal(): void {
        this.resetModal();
        this.$modal.close();
    }
    protected resetModal(): void {
        if(this.$form && this.$form.reset) {
            this.$form.reset();
        }
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.changes && Array.isArray(detail.changes)) {
            const count = (detail.changes as []).length;
            if(count > 0) {
                this.toaster.standard.added('am:assessment.$', true, count);
            }
        } else {
            this.toaster.standard.added('am:assessment.$', true);
        }

        this.$modal.close();
        this.reload();
    }

    protected openIcccAttachmentModal(attachmentId: string): void {
        this.icccAttachmentId = attachmentId;
        this.$icccAttachmentModal.open();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.externalAssessmentAccess = {
            cci: this.accountContext.hasRouteAccess('cci.assessment'),
            iccc: this.accountContext.hasRouteAccess('iccc.assessment')
        } as IExternalAssessmentAccess;
    }

    protected openIccc(assessmentAttachment: CM.CaseAssessmentAttachment): void {
        if(assessmentAttachment.externalContent?.mediaType?.code !== 'data.xml' || !assessmentAttachment.externalContent.content) {
            return;
        }

        const content = assessmentAttachment.externalContent.content || "";

        // parse xml
        const parser = new DOMParser();
        const review: Document = parser.parseFromString(content, "text/xml");

        const uuid = review.getElementsByTagName('CarePlan')[0].getAttribute("UUID");
        if(uuid) {
            this.iccc.open(this.context.case, assessmentAttachment, uuid);
        }
    }

    protected openPlanOfCare(attachment: CM.CaseAssessmentAttachment, isCompleted: boolean, versionName: string): void {
        if(!attachment.id) return;

        // Set copyright text based on selected assessment
        const copyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();
        const edition = versionName ? versionName.split('.')[0] : getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);

        // Set custom content disclaimer based on parsed content
        if(attachment.assessment?.externalSystem?.code?.toLowerCase() === 'cci' && attachment.externalContent?.content) {
            const content = attachment.externalContent.content;
            const assessment: Mcg.Assessment = JSON.parse(content);
            this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
        } else {
            this.mcgCustomContentDisclaimer = undefined;
        }

        this.$mcgPlanOfCare.open(attachment.id, this.$mcgCarePlanModal, isCompleted);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAttachments(): Promise<void> {
        if(!this.targetId) return;

        await this.checkPermissions();
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        let includes: string[] = [
            'assessment.currentVersion',
            'lastSession',
            'lastSession.metadata.modified.byUser',
            'metadata.activeChanged.byUser',
            'metadata.modified.byUser',
            'task'
        ];

        let fields: string[] = [
            'description',
            'isCompleted',
            'isRequired',
            'lastSession.metadata.modified.byUser.description',
            'lastSession.metadata.modified.timestamp',
            'metadata.modified.timestamp',
            'metadata.modified.byUser.name.full',
            'metadata.activeChanged.byUser.name.full',
            'metadata.activeChanged.timestamp',
            'task.attachedTo.type.code',
            'task.id',
            'task.isActive',
            'taskKeyCode',
            'taskName'
        ];

        if(this.externalAssessmentAccess?.cci || this.externalAssessmentAccess?.iccc) {
            includes.push(...['assessment', 'assessment.externalSystem', 'externalContent', 'externalContent.mediaType']);
            fields.push(...[
                'assessment.currentVersion.versionName',
                'assessment.externalSystem.code',
                'assessment.externalSystem.shortName',
                'externalContent.content',
                'externalcontent.isContentEncrypted',
                'externalContent.mediaType.code'
            ]);
        }

        try {
            this.state.beginLoad();
            const query = this.service.createAttachmentsByTargetQuery(this.targetId, options, includes, null, fields);

            if(!this.includeInactive) {
                query.filterOr((f) => {
                    f.filterIsNull('task.id');
                    f.filterBoolean('task.isActive', true);
                });
            }
            const response = await this.service.get<CM.CaseAssessmentAttachment>(query);
            this.attachments = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.events.publish(new ReturnFocusEvent());
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadAllAttachments(): Promise<void> {
        if(!this.targetId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields: string[] = [
            'assessment.id',
            'isCompleted'
        ];

        try {
            this.state.beginLoad();
            const query = this.service.createAttachmentsByTargetQuery(this.targetId, options, [], null, fields);
            const response = await this.service.get<CM.CaseAssessmentAttachment>(query);
            this.allAttachments = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateAttachment(attachmentId: string, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('am:assessment.$');
            if(result.dialogResponse === confirmation.options.CANCEL) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAttachment(attachmentId, isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('am:assessment.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
